/**
 * Boîte à outils permettant d'enregistrer et d'obtenir facilement des animations réutilisables de différents types.
 */
export default class FF_Animations {

  utils = {
    is_gsap_animation: (animation) => animation instanceof TweenLite || animation instanceof TimelineLite || animation instanceof TweenMax || animation instanceof TimelineMax // eslint-disable-line no-autofix/snakecasejs/snakecasejs
  };

  /***
   *      ####   ####  #    #  ####  ##### #####  #    #  ####  #####  ####  #####
   *     #    # #    # ##   # #        #   #    # #    # #    #   #   #    # #    #
   *     #      #    # # #  #  ####    #   #    # #    # #        #   #    # #    #
   *     #      #    # #  # #      #   #   #####  #    # #        #   #    # #####
   *      ####   ####  #    #  ####    #   #    #  ####   ####    #    ####  #    #
   */
  /**
   * @constructor
   * @param {Object} [options={}] Options. Optionnel.
   * @param {Boolean} [options.disable_animation_overwite_warnings=false] Si oui ou non les avertissements de "overwrite" d'animation d'élément enregistrés doivent être affichés dans la console. Optionnel.
   * @param {Boolean} [options.disable_page_animations=false] Si oui ou non les animations de pages doivent être désactivés. Utiles lors du développement. Optionnel.
   */
  constructor(options = {}) {

    // Propriétés
    this.id_animations = [];
    this.scroll_trigger_animations_callbacks = [];
    this.scroll_trigger_animations = [];
    this.page_animations = {
      load : null,
      leave: null,
      enter: null
    };
    this.options = {
      disable_animation_overwite_warnings: false,
      disable_page_animations            : false,
      ...options
    };

    // Warning si les animations de page sont désactivés
    if (this.options.disable_page_animations) {
      console.warn('FF_Animations: Les animations de pages sont présentement désactivées.');
    }

    // Gestion des animations ScrollTrigger au "beforeEnter"
    if (typeof barba === 'object' && typeof barba.hooks === 'object') {
      barba.hooks.beforeEnter(() => {
        ScrollTrigger.clearScrollMemory();
        ScrollTrigger.clearMatchMedia();
        this.scroll_trigger_animations.forEach((animation) => {
          if (typeof animation.revert === 'function') animation.revert();
          if (typeof animation.kill === 'function') animation.kill();
        });
        this.scroll_trigger_animations = [];
        setTimeout(() => {
          this.init_scroll_trigger_animations();
        });
      });
    }

  }

  /***
   *      ####  ###### #####      ##   #    # # #    #   ##   ##### #  ####  #    #
   *     #    # #        #       #  #  ##   # # ##  ##  #  #    #   # #    # ##   #
   *     #      #####    #      #    # # #  # # # ## # #    #   #   # #    # # #  #
   *     #  ### #        #      ###### #  # # # #    # ######   #   # #    # #  # #
   *      ####  ######   #      #    # #    # # #    # #    #   #   #  ####  #    #
   */
  /**
   * Retourne une timeline d'animation réutilisable selon un ID d'animation et un élément donné. Les animations doiveent être définies avec this.register_element_animation() avec un ID unique.
   * @param {String} animation_id ID de l'animation à retourner.
   * @param {HTMLElement} element Élément pour lequel appliquer l'animation.
   * @param {...*} args Arguments à passer à la fonction "callback" donnée dans la méthode register_animation() pour l'ID associé.
   * @returns {Object|null} Timeline d'animation associé à l'ID donné.
   */
  get_animation(animation_id, element, ...args) {

    if (!this.id_animations[animation_id]) {
      console.error(`L'animation pour l'ID "${animation_id}" n'a pas encore été déclarée. Il faut la déclarer avec la méthode register_animation().`);
      return null;
    }

    let animation;
    gsap.context(() => {
      animation = this.id_animations[animation_id](element, ...args);
    }, element);

    if (!animation || !this.utils.is_gsap_animation(animation)) {
      console.error(`Le paramètre donné à la méthode register_animation() pour l'ID "${animation_id}" doit absolument être un fonction qui retourne une animation ou une timeline GSAP.`);
      return null;
    }
    return animation;

  }

  /***
   *     #####  ######  ####  #  ####  ##### ###### #####       ##   #    # # #    #   ##   ##### #  ####  #    #
   *     #    # #      #    # # #        #   #      #    #     #  #  ##   # # ##  ##  #  #    #   # #    # ##   #
   *     #    # #####  #      #  ####    #   #####  #    #    #    # # #  # # # ## # #    #   #   # #    # # #  #
   *     #####  #      #  ### #      #   #   #      #####     ###### #  # # # #    # ######   #   # #    # #  # #
   *     #    # ######  ####  #  ####    #   ###### #    #    #    # #    # # #    # #    #   #   #  ####  #    #
   */
  /**
   * Inscrit une animation définit dans un callback à un ID afin de pouvoir l'appeler plus tard avec la méthode this.get_element_animation().
   * @param {String} animation_id ID pour lequel inscrire l'animation.
   * @param {Function} callback Fonction qui définit les étapes de l'animation. Elle possède, comme premier paramètre, l'élément "element donné comme deuxième argument à la méthode this.get_element_animation() ainsi que les autres argument donnés par la suite "..args". (element, ...args) => { }.
   */
  register_animation(animation_id, callback) {

    // Avertissement si l'animation a déjà été enregistré
    if (this.id_animations[animation_id] && !this.options.disable_animation_overwite_warnings) {
      console.warn(`FF_Animations : L'animation d'élément avec l'ID "${animation_id}" existe déjà et sera donc écrasée.`);
    }

    // Bail si le callback renseigné n'est pas un fonction
    if (typeof callback !== 'function') {
      console.error('Le paramètre donné à la méthode register_animation() doit absolument être un fonction qui retourne une animation ou une timeline GSAP.');
      return;
    }

    // Store l'animation
    this.id_animations[animation_id] = callback;

  }

  /***
   *     #####  ######  ####  #  ####  ##### ###### #####      ####   ####  #####   ####  #      #         ##### #####  #  ####   ####  ###### #####       ##   #    # # #    #   ##   ##### #  ####  #    #
   *     #    # #      #    # # #        #   #      #    #    #      #    # #    # #    # #      #           #   #    # # #    # #    # #      #    #     #  #  ##   # # ##  ##  #  #    #   # #    # ##   #
   *     #    # #####  #      #  ####    #   #####  #    #     ####  #      #    # #    # #      #           #   #    # # #      #      #####  #    #    #    # # #  # # # ## # #    #   #   # #    # # #  #
   *     #####  #      #  ### #      #   #   #      #####          # #      #####  #    # #      #           #   #####  # #  ### #  ### #      #####     ###### #  # # # #    # ######   #   # #    # #  # #
   *     #    # ######  ####  #  ####    #   ###### #    #     ####   ####  #    #  ####  ###### ######      #   #    # #  ####   ####  ###### #    #    #    # #    # # #    # #    #   #   #  ####  #    #
   */
  /**
   * Inscrit une animation ScrollTrigger définit dans un callback donné pour un sélecteur jQuery donné.
   * @param {String} selector Sélecteur jQuery des éléments pour lesquelles initialiser l'animation.
   * @param {Function} callback Fonction retournant soit un tween ou une timeline GSAP à inscrire. Elle possède comme premier argument l'élément "element" à animer correspondant au sélecteur. (element) => { }.
   */
  register_scroll_trigger_animation(selector, callback) {

    // Bail si le callback renseigné n'est pas un fonction
    if (typeof callback !== 'function') {
      console.error('Le paramètre donné à la méthode register_animation() doit absolument être un fonction qui retourne une animation ou une timeline GSAP.');
      return;
    }

    // Store l'animation
    this.scroll_trigger_animations_callbacks.push({ selector, callback });

  }

  /***
   *     # #    # # #####     ####   ####  #####   ####  #      #         ##### #####  #  ####   ####  ###### #####       ##   #    # # #    #   ##   ##### #  ####  #    #  ####
   *     # ##   # #   #      #      #    # #    # #    # #      #           #   #    # # #    # #    # #      #    #     #  #  ##   # # ##  ##  #  #    #   # #    # ##   # #
   *     # # #  # #   #       ####  #      #    # #    # #      #           #   #    # # #      #      #####  #    #    #    # # #  # # # ## # #    #   #   # #    # # #  #  ####
   *     # #  # # #   #           # #      #####  #    # #      #           #   #####  # #  ### #  ### #      #####     ###### #  # # # #    # ######   #   # #    # #  # #      #
   *     # #    # #   #       ####   ####  #    #  ####  ###### ######      #   #    # #  ####   ####  ###### #    #    #    # #    # # #    # #    #   #   #  ####  #    #  ####
   */
  /**
   * Instancie les animations ScrollTrigger précédamment enregistrées avec la méthode register_scroll_trigger_animation();.
   * @param {HTMLElement} container Conteneur dans lequel les éléments des animation se trouvent.
   */
  init_scroll_trigger_animations(container = document.body) {
    this.scroll_trigger_animations_callbacks.forEach((callback_obj) => {
      this.init_scroll_trigger_animation(callback_obj, container);
    });
  }

  /***
   *     # #    # # #####     ####   ####  #####   ####  #      #         ##### #####  #  ####   ####  ###### #####       ##   #    # # #    #   ##   ##### #  ####  #    #
   *     # ##   # #   #      #      #    # #    # #    # #      #           #   #    # # #    # #    # #      #    #     #  #  ##   # # ##  ##  #  #    #   # #    # ##   #
   *     # # #  # #   #       ####  #      #    # #    # #      #           #   #    # # #      #      #####  #    #    #    # # #  # # # ## # #    #   #   # #    # # #  #
   *     # #  # # #   #           # #      #####  #    # #      #           #   #####  # #  ### #  ### #      #####     ###### #  # # # #    # ######   #   # #    # #  # #
   *     # #    # #   #       ####   ####  #    #  ####  ###### ######      #   #    # #  ####   ####  ###### #    #    #    # #    # # #    # #    #   #   #  ####  #    #
   */
  /**
   * Instancie les animations ScrollTrigger associé à un objet de callback précédamment enregistrées avec la méthode register_scroll_trigger_animation().
   * @param {Object} callback_obj Objet dans le tableau this.scroll_trigger_animations_callbacks du sélecteur et du callback d'animation à initialiser.
   * @param {HTMLElement} container Conteneur dans lequel les éléments des animation se trouvent.
   */
  init_scroll_trigger_animation(callback_obj, container = document.body) {
    $(container).find(callback_obj.selector).each((element_i, element) => {

      const context = gsap.context(() => {
        callback_obj.callback(element);
      }, element);

      // Retrouve, dans le context, tous les tweens et les timelines qui possède une configuration ScrollTrigger ainsi que les objets ScrollTrigger qui ne sont pas associés aux autres animations
      let animations = context.data.filter((data) => typeof data.scrollTrigger !== 'undefined' || data instanceof ScrollTrigger); // eslint-disable-line no-autofix/snakecasejs/snakecasejs
      animations = animations.filter((data) => {
        if (!(data instanceof ScrollTrigger)) return true; // eslint-disable-line no-autofix/snakecasejs/snakecasejs
        return !animations.some((sub_data) => typeof sub_data.scrollTrigger !== 'undefined' && sub_data.scrollTrigger == data); // Si le ScrollTrigger est associé a une animations, on le retire du tableau
      });

      // Bail si l'animation n'est pas une animation GSAP ou un objet ScrollTrigger
      if (!animations) { // eslint-disable-line no-autofix/snakecasejs/snakecasejs
        console.error(`Le paramètre donné à la méthode register_scroll_trigger_animation() pour le sélecteur "${callback_obj.selector}" doit absolument être une fonction qui déclare une animation ou une timeline GSAP contenant une configuration ScrollTrigger ou une instance ScrollTrigger.`);
        return;
      }

      // Ajout de l'animation au tableau
      animations.forEach((animation) => this.scroll_trigger_animations.push(animation));

    });
  }

  /***
   *     # #    # # #####     ####  ###### #      ######  ####  #####  ####  #####      ####   ####  #####   ####  #      #         ##### #####  #  ####   ####  ###### #####       ##   #    # # #    #   ##   ##### #  ####  #    #  ####
   *     # ##   # #   #      #      #      #      #      #    #   #   #    # #    #    #      #    # #    # #    # #      #           #   #    # # #    # #    # #      #    #     #  #  ##   # # ##  ##  #  #    #   # #    # ##   # #
   *     # # #  # #   #       ####  #####  #      #####  #        #   #    # #    #     ####  #      #    # #    # #      #           #   #    # # #      #      #####  #    #    #    # # #  # # # ## # #    #   #   # #    # # #  #  ####
   *     # #  # # #   #           # #      #      #      #        #   #    # #####          # #      #####  #    # #      #           #   #####  # #  ### #  ### #      #####     ###### #  # # # #    # ######   #   # #    # #  # #      #
   *     # #    # #   #       ####  ###### ###### ######  ####    #    ####  #    #     ####   ####  #    #  ####  ###### ######      #   #    # #  ####   ####  ###### #    #    #    # #    # # #    # #    #   #   #  ####  #    #  ####
   */
  /**
   * Initialise les animations inscrites avec register_scroll_trigger_animation() pour un sélecteur donné.
   * @param {String} selector Sélecteur jQuery des éléments pour lesquels initialiser les animations inscrites.
   */
  init_selector_scroll_trigger_animations(selector) {

    const selector_scroll_trigger_animations_callbacks = this.scroll_trigger_animations_callbacks.filter((callback) => callback.selector == selector);
    if (!selector_scroll_trigger_animations_callbacks) {
      return;
    }

    // Initialise toutes les animations trouvées
    selector_scroll_trigger_animations_callbacks.forEach((callback_obj) => {
      this.init_scroll_trigger_animation(callback_obj);
    });

  }


  /***
   *     #####  ######  ####  #  ####  ##### ###### #####     #####    ##    ####  ######    #       ####    ##   #####       ##   #    # # #    #   ##   ##### #  ####  #    #
   *     #    # #      #    # # #        #   #      #    #    #    #  #  #  #    # #         #      #    #  #  #  #    #     #  #  ##   # # ##  ##  #  #    #   # #    # ##   #
   *     #    # #####  #      #  ####    #   #####  #    #    #    # #    # #      #####     #      #    # #    # #    #    #    # # #  # # # ## # #    #   #   # #    # # #  #
   *     #####  #      #  ### #      #   #   #      #####     #####  ###### #  ### #         #      #    # ###### #    #    ###### #  # # # #    # ######   #   # #    # #  # #
   *     #    # ######  ####  #  ####    #   ###### #    #    #      #    #  ####  ######    ######  ####  #    # #####     #    # #    # # #    # #    #   #   #  ####  #    #
   */
  /**
   * Déclare l'animation de chargement de page avec une fonction "callback" donnée qui retourne une animation ou une timeline GSAP.
   * @param {Function} callback Fonction qui retourne une animation ou une timeline GSAP.
   */
  register_page_load_animation(callback) {

    // Bail si l'animation a déjà été enregistré
    if (this.page_animations.load) {
      console.warn('L\'animation de chargement de page a déjà été déclarée.');
      return;
    }

    // Bail si le callback renseigné n'est pas un fonction
    if (typeof callback !== 'function') { // eslint-disable-line no-autofix/snakecasejs/snakecasejs
      console.error('Le paramètre donné à la méthode register_page_load_animation() doit absolument être un fonction qui retourne une animation ou une timeline GSAP.');
      return;
    }

    // Store l'animation
    this.page_animations.load = callback;

  }

  /***
   *      ####  ###### #####    #####    ##    ####  ######    #       ####    ##   #####       ##   #    # # #    #   ##   ##### #  ####  #    #
   *     #    # #        #      #    #  #  #  #    # #         #      #    #  #  #  #    #     #  #  ##   # # ##  ##  #  #    #   # #    # ##   #
   *     #      #####    #      #    # #    # #      #####     #      #    # #    # #    #    #    # # #  # # # ## # #    #   #   # #    # # #  #
   *     #  ### #        #      #####  ###### #  ### #         #      #    # ###### #    #    ###### #  # # # #    # ######   #   # #    # #  # #
   *      ####  ######   #      #      #    #  ####  ######    ######  ####  #    # #####     #    # #    # # #    # #    #   #   #  ####  #    #
   */
  /**
   * Retourne l'animation de chargement de page qui a été déclarée dans la méthode register_page_load_animation().
   * @param {...*} args Arguments à passer à la fonction "callback" donnée dans la méthode register_page_load_animation().
   * @returns {Object|null} Animation ou timeline GSAP de l'animation.
   */
  get_page_load_animation(...args) {

    if (!this.page_animations.load) {
      console.error('L\'animation de chargement de page n\'a pas encore été déclarée. Il faut la déclarer avec la méthode register_page_load_animation().');
      return null;
    }

    // Si les animations de pages sont désactivés, on retourne une timeline vide
    if (this.options.disable_page_animations) {
      $('html').addClass('first-load-anim-completed');
      gsap.set('main', { opacity: 1 });
      return gsap.timeline().set({}, {}, 0.015);
    }

    // Retour de l'animation enregistrée
    const animation = this.page_animations.load(...args);
    if (!animation || !this.utils.is_gsap_animation(animation)) {
      console.error('Le paramètre donné à la méthode register_page_load_animation() doit absolument être un fonction qui retourne une animation ou une timeline GSAP.');
      return null;
    }
    return animation;

  }

  /***
   *     #####  ######  ####  #  ####  ##### ###### #####     #####    ##    ####  ######    #      ######   ##   #    # ######      ##   #    # # #    #   ##   ##### #  ####  #    #
   *     #    # #      #    # # #        #   #      #    #    #    #  #  #  #    # #         #      #       #  #  #    # #          #  #  ##   # # ##  ##  #  #    #   # #    # ##   #
   *     #    # #####  #      #  ####    #   #####  #    #    #    # #    # #      #####     #      #####  #    # #    # #####     #    # # #  # # # ## # #    #   #   # #    # # #  #
   *     #####  #      #  ### #      #   #   #      #####     #####  ###### #  ### #         #      #      ###### #    # #         ###### #  # # # #    # ######   #   # #    # #  # #
   *     #    # ######  ####  #  ####    #   ###### #    #    #      #    #  ####  ######    ###### ###### #    #   ##   ######    #    # #    # # #    # #    #   #   #  ####  #    #
   */
  /**
   * Déclare l'animation de sortie de page avec une fonction "callback" donnée qui retourne une animation ou une timeline GSAP.
   * @param {Function} callback Fonction qui retourne une animation ou une timeline GSAP.
   */
  register_page_leave_animation(callback) {

    // Bail si l'animation a déjà été enregistré
    if (this.page_animations.leave) {
      console.warn('L\'animation de sortie de page a déjà été déclarée.');
      return;
    }

    // Bail si le callback renseigné n'est pas un fonction
    if (typeof callback !== 'function') { // eslint-disable-line no-autofix/snakecasejs/snakecasejs
      console.error('Le paramètre donné à la méthode register_page_leave_animation() doit absolument être un fonction qui retourne une animation ou une timeline GSAP.');
      return;
    }

    // Store l'animation
    this.page_animations.leave = callback;

  }


  /***
   *      ####  ###### #####    #####    ##    ####  ######    #      ######   ##   #    # ######      ##   #    # # #    #   ##   ##### #  ####  #    #
   *     #    # #        #      #    #  #  #  #    # #         #      #       #  #  #    # #          #  #  ##   # # ##  ##  #  #    #   # #    # ##   #
   *     #      #####    #      #    # #    # #      #####     #      #####  #    # #    # #####     #    # # #  # # # ## # #    #   #   # #    # # #  #
   *     #  ### #        #      #####  ###### #  ### #         #      #      ###### #    # #         ###### #  # # # #    # ######   #   # #    # #  # #
   *      ####  ######   #      #      #    #  ####  ######    ###### ###### #    #   ##   ######    #    # #    # # #    # #    #   #   #  ####  #    #
   */
  /**
   * Retourne l'animation de sortie de page qui a été déclarée dans la méthode register_page_leave_animation().
   * @param {...*} args Arguments à passer à la fonction "callback" donnée dans la méthode register_page_leave_animation().
   * @returns {Object|null} Animation ou timeline GSAP de l'animation.
   */
  get_page_leave_animation(...args) {

    if (!this.page_animations.leave) {
      console.error('L\'animation de sortie de page n\'a pas encore été déclarée. Il faut la déclarer avec la méthode register_page_leave_animation().');
      return null;
    }

    // Si les animations de pages sont désactivés, on retourne une timeline vide
    if (this.options.disable_page_animations) {
      gsap.set('main, .footer', { opacity: 1 });
      return gsap.timeline();
    }

    // Retour de l'animation enregistrée
    const animation = this.page_animations.leave(...args);
    if (!animation || !this.utils.is_gsap_animation(animation)) {
      console.error('Le paramètre donné à la méthode register_page_leave_animation() doit absolument être un fonction qui retourne une animation ou une timeline GSAP.');
      return null;
    }
    return animation;

  }

  /***
   *     #####  ######  ####  #  ####  ##### ###### #####     #####    ##    ####  ######    ###### #    # ##### ###### #####       ##   #    # # #    #   ##   ##### #  ####  #    #
   *     #    # #      #    # # #        #   #      #    #    #    #  #  #  #    # #         #      ##   #   #   #      #    #     #  #  ##   # # ##  ##  #  #    #   # #    # ##   #
   *     #    # #####  #      #  ####    #   #####  #    #    #    # #    # #      #####     #####  # #  #   #   #####  #    #    #    # # #  # # # ## # #    #   #   # #    # # #  #
   *     #####  #      #  ### #      #   #   #      #####     #####  ###### #  ### #         #      #  # #   #   #      #####     ###### #  # # # #    # ######   #   # #    # #  # #
   *     #    # ######  ####  #  ####    #   ###### #    #    #      #    #  ####  ######    ###### #    #   #   ###### #    #    #    # #    # # #    # #    #   #   #  ####  #    #
   */
  /**
   * Déclare l'animation d'entrée de page avec une fonction "callback" donnée qui retourne une animation ou une timeline GSAP.
   * @param {Function} callback Fonction qui retourne une animation ou une timeline GSAP.
   */
  register_page_enter_animation(callback) {

    // Bail si l'animation a déjà été enregistré
    if (this.page_animations.enter) {
      console.warn('L\'animation d\'entrée de page a déjà été déclarée.');
      return;
    }

    // Bail si le callback renseigné n'est pas un fonction
    if (typeof callback !== 'function') { // eslint-disable-line no-autofix/snakecasejs/snakecasejs
      console.error('Le paramètre donné à la méthode register_page_enter_animation() doit absolument être un fonction qui retourne une animation ou une timeline GSAP.');
      return;
    }

    // Store l'animation
    this.page_animations.enter = callback;

  }


  /***
   *      ####  ###### #####    #####    ##    ####  ######    ###### #    # ##### ###### #####       ##   #    # # #    #   ##   ##### #  ####  #    #
   *     #    # #        #      #    #  #  #  #    # #         #      ##   #   #   #      #    #     #  #  ##   # # ##  ##  #  #    #   # #    # ##   #
   *     #      #####    #      #    # #    # #      #####     #####  # #  #   #   #####  #    #    #    # # #  # # # ## # #    #   #   # #    # # #  #
   *     #  ### #        #      #####  ###### #  ### #         #      #  # #   #   #      #####     ###### #  # # # #    # ######   #   # #    # #  # #
   *      ####  ######   #      #      #    #  ####  ######    ###### #    #   #   ###### #    #    #    # #    # # #    # #    #   #   #  ####  #    #
   */
  /**
   * Retourne l'animation de sortie de page qui a été déclarée dans la méthode register_page_leave_animation().
   * @param {...*} args Arguments à passer à la fonction "callback" donnée dans la méthode register_page_leave_animation().
   * @returns {Object|null} Animation ou timeline GSAP de l'animation.
   */
  get_page_enter_animation(...args) {

    if (!this.page_animations.leave) {
      console.error('L\'animation d\'entrée de page n\'a pas encore été déclarée. Il faut la déclarer avec la méthode register_page_enter_animation().');
      return null;
    }

    // Si les animations de pages sont désactivés, on retourne une timeline vide
    if (this.options.disable_page_animations) {
      gsap.set('main, .footer', { opacity: 1 });
      return gsap.timeline();
    }

    // Retour de l'animation enregistrée
    const animation = this.page_animations.enter(...args);
    if (!animation || !this.utils.is_gsap_animation(animation)) {
      console.error('Le paramètre donné à la méthode register_page_enter_animation() doit absolument être un fonction qui retourne une animation ou une timeline GSAP.');
      return null;
    }
    return animation;

  }

}
